import { createFileRoute, redirect } from '@tanstack/react-router'
import { AuthenticatedLayout } from '@/components/layout/authenticated-layout'
import { useAuthStore } from '@/stores/auth-store'
import { supabase } from '@/lib/supabase'
import { isBcOnlyUser, ROUTES } from '@/lib/route-guards'

export const Route = createFileRoute('/_authenticated')({
  beforeLoad: async ({ location }) => {
    const waitForAuth = async (maxWait = 5000): Promise<boolean> => {
      const startTime = Date.now()

      while (Date.now() - startTime < maxWait) {
        const { auth: authState } = useAuthStore.getState()

        if (!authState.isLoading) {
          return !!authState.user
        }

        // Fallback: check Supabase session directly
        const { data: { session } } = await supabase.auth.getSession()
        if (session?.user) {
          return true
        }

        await new Promise((resolve) => setTimeout(resolve, 100))
      }

      const { auth: authState } = useAuthStore.getState()
      return !!authState.user
    }

    const isAuthenticated = await waitForAuth()

    if (!isAuthenticated) {
      const redirectUrl = encodeURIComponent(location.href)
      throw redirect({
        to: '/sign-in',
        href: `/sign-in?redirect=${redirectUrl}`,
      })
    }

    // BC-only users: redirect to /background-check if accessing non-BC routes
    const bcOnly = await isBcOnlyUser()
    if (bcOnly && !location.pathname.startsWith('/background-check')) {
      throw redirect({ to: ROUTES.BC_DEFAULT, replace: true })
    }
  },
  component: AuthenticatedLayout,
})
